// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// The ladder itself is fixed by the algorithm -- each rung is half the disparity of the last --
// so it's drawn from this constant rather than derived from the payload. Overridden by
// modules.StereoLadder.ladderArcsec when the report carries it (algorithm 1.3.0+).
export const RUNGS = [800, 400, 200, 140, 100, 80, 60, 50, 40];
// The clinical convention for "normal" stereoacuity, not a value the summary payload carries.
const NORMAL_CEILING = 60;

const PASSED_BG = "#22BE3920";
const PASSED_COLOR = "#22BE39";
const FAILED_BG = "#E10C0C20";
const FAILED_COLOR = "#E10C0C";
const NOT_REACHED_BG = "#5B6D7A15";
const NOT_REACHED_COLOR = "#000";
const THRESHOLD_BORDER = "#22BE39";
const NORMAL_MARKER = "#5AA9E680";

type StereoLadderLevelType = {
  disparityArcsec: number;
  // Only present from algorithm 1.3.0 onward -- older reports fall back to deriving it from
  // trials below.
  passed?: boolean;
  trials: Array<{ correct: boolean }>;
};

type StereoLadderChartType = {
  thresholdArcseconds: number | null;
  levels?: Array<StereoLadderLevelType>;
  ladderArcsec?: Array<number>;
};

const StereoLadderChart = ({
  thresholdArcseconds,
  levels,
  ladderArcsec,
}: StereoLadderChartType) => {
  const { t } = useTranslation();

  const rungs = ladderArcsec && ladderArcsec.length > 0 ? ladderArcsec : RUNGS;

  return (
    <Box sx={{ borderBottom: "1px solid #5B6D7A40", pb: "8px", mb: "8px" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
          gap: "4px",
        }}
      >
        {rungs.map((rung) => {
          const level = levels?.find(
            (candidate) => candidate.disparityArcsec === rung
          );
          const isThresholdRung =
            thresholdArcseconds !== null && rung === thresholdArcseconds;
          const isNormal = rung <= NORMAL_CEILING;

          const correctCount =
            level?.trials.filter((trial) => trial.correct).length ?? 0;
          const totalCount = level?.trials.length ?? 0;
          const passed = level
            ? level.passed ?? (totalCount > 0 && correctCount === totalCount)
            : false;
          const notReached = !level;

          return (
            <Box
              key={rung}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "4px",
                borderRadius: "4px",
                border: `2px solid ${isThresholdRung ? THRESHOLD_BORDER : "transparent"}`,
                backgroundColor: notReached
                  ? isNormal
                    ? NORMAL_MARKER
                    : NOT_REACHED_BG
                  : passed
                    ? PASSED_BG
                    : FAILED_BG,
                color: notReached
                  ? NOT_REACHED_COLOR
                  : passed
                    ? PASSED_COLOR
                    : FAILED_COLOR,
              }}
            >
              <Typography
                fontSize="14px"
                fontWeight={600}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {rung}″
              </Typography>
              <Typography fontSize="10px" textAlign="center">
                {notReached
                  ? t("Not Reached")
                  : `${passed ? t("Passed") : t("Failed")} ${correctCount}/${totalCount}`}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        gap="8px"
        sx={{ mt: "6px" }}
      >
        <Typography fontSize="12px" color="#5B6D7A">
          {t("coarse · large depth, easy")}
        </Typography>
        <Typography
          fontSize="12px"
          color="#5B6D7A"
          sx={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <Box
            component="span"
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: NORMAL_MARKER,
            }}
          />
          {t("normal range")} ≤ {NORMAL_CEILING}″
        </Typography>
        <Typography fontSize="12px" color="#5B6D7A">
          {t("fine · small depth, hard")}
        </Typography>
      </Box>
    </Box>
  );
};

export default StereoLadderChart;
